import React from 'react';
import ReactDOM from 'react-dom';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import {
  Button,
  EmbeddedVideo,
  Form,
  FormField,
  FormSchemaEntry,
  HTMLParser,
  IImagePickerRef,
  Image,
  ImagePicker,
  KeepProportions,
  LoadingSpinner,
  SvgIcon,
  UI,
} from '@gik/ui';
import { addUserUpdateSchema, addVideoFormSchema } from '@gik/inkind-page/formSchemas';
import { createUserUpdate, updateUserUpdate, updateUserUpdateMedia } from '@gik/inkind-page/api/UserUpdatesAPI';
import InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import { useList } from 'react-use';
import InkindUserUpdateMedia from '@gik/inkind-page/models/InkindUserUpdateMedia';
import { extractYouTubeVideoIdFromURL, isYouTubeUrl } from '@gik/core/utils/extractVideoId';
import { getVimeoThumbnailUrl } from '@gik/inkind-page/api/VimeoAPI';
import moment from 'moment';
import { createFacebookShareUrl, createTwitterShareUrl, getUserUpdateLink } from '@gik/core/utils/ShareUtils';
import { openExternalLink } from '@gik/core/utils/browser';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import coreTranslationKeys from '@gik/i18n/en/core';
import { useTranslation } from 'react-i18next';
import { AddUserUpdateInputNames } from '@gik/inkind-page/enums/AddUserUpdateInputNames';
import { AddVideoInputNames } from '@gik/inkind-page/enums/AddVideoInputNames';

interface IAddEditInkindUserUpdatesModalProps {
  routeId?: string;
  isOpen?: boolean;
  onClose?: () => void;
  selectedUpdate?: InkindUserUpdate;
  buttonsPortal?: React.MutableRefObject<HTMLDivElement>;
}

const blockName = 'add-edit-inkind-user-updates-modal';

export interface IAddEditInkindUserUpdatesForm {
  [AddUserUpdateInputNames.DATE]: number;
  [AddUserUpdateInputNames.TITLE]: string;
  [AddUserUpdateInputNames.CONTENT]: string;
  [AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: boolean;
  [AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]: boolean;
  [AddUserUpdateInputNames.SEND_OPTIONS_FACEBOOK]: boolean;
  [AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: boolean;
}

export default function AddEditInkindUserUpdatesModal({
  routeId,
  onClose = noop,
  selectedUpdate,
  buttonsPortal,
}: IAddEditInkindUserUpdatesModalProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const [value, setValue] = React.useState<Partial<IAddEditInkindUserUpdatesForm>>(
    selectedUpdate
      ? inkindUserUpdateToAddEditInkindUserUpdatesForm(selectedUpdate)
      : {
          [AddUserUpdateInputNames.DATE]: Date.now(),
          [AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: true,
          [AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]: true,
        }
  );

  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadedMedia, uploadedMediaHandler] = useList<InkindUserUpdateMedia>(selectedUpdate?.media || []);
  const mediaToBeRemoved = React.useRef<string[]>([]);
  const caption = React.useRef<string>();
  const videoLink = React.useRef<string>();
  const imagePickerRef = React.useRef<IImagePickerRef>(null);

  const schema = addUserUpdateSchema();

  // TODO: move this
  async function getThumbnailUrl(videoLink: string) {
    if (isYouTubeUrl(videoLink)) {
      return `https://img.youtube.com/vi/${extractYouTubeVideoIdFromURL(videoLink)}/default.jpg`;
    } else {
      return getVimeoThumbnailUrl(videoLink);
    }
  }

  function inkindUserUpdateToAddEditInkindUserUpdatesForm(
    update: InkindUserUpdate
  ): Partial<IAddEditInkindUserUpdatesForm> {
    return {
      [AddUserUpdateInputNames.DATE]: moment(update.publishedDate).valueOf(),
      [AddUserUpdateInputNames.TITLE]: update.title,
      [AddUserUpdateInputNames.CONTENT]: update.description,
      [AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: true,
    };
  }

  const shareRef = React.useRef<HTMLDivElement>();

  async function handlePostUpdate() {
    setLoading(true);
    if (selectedUpdate) {
      await updateUserUpdate(selectedUpdate.id, value);
      await updateUserUpdateMedia(selectedUpdate.id, mediaToBeRemoved.current, uploadedMedia);
    } else {
      const updateId = await createUserUpdate(routeId, value, uploadedMedia);

      if (value[AddUserUpdateInputNames.SEND_OPTIONS_FACEBOOK] || value[AddUserUpdateInputNames.SEND_OPTIONS_TWITTER]) {
        await UI.dialog(
          <ShareUpdateOnSocialMedia
            updateId={updateId}
            shareOnFacebook={value[AddUserUpdateInputNames.SEND_OPTIONS_FACEBOOK]}
            shareOnTwitter={value[AddUserUpdateInputNames.SEND_OPTIONS_TWITTER]}
            sharedToEmail={value[AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]}
            buttonsRef={shareRef}
          />,
          {
            title: t(translationKeys.ShareUpdateOnSocialMediaTitle),
            buttons: <div ref={shareRef} />,
          }
        );
      }
    }
    UI.closeDialog();
    setLoading(false);
    onClose();
  }

  async function openAddMediaModal() {
    let close: Function;
    await UI.dialog(
      ({ close: _close }) => {
        close = _close;
        return (
          <div {...bem('add-media-modal')}>
            <ImagePicker
              {...bem('image-picker')}
              ref={imagePickerRef}
              noPreview
              addButtonText={t(translationKeys.AddMediaModalAddButtonText)}
              onChange={croppedImage => croppedImage && openConfirmImageModal(croppedImage, _close)}
            />
            <Button onClick={() => openAddVideoModal(_close)}>Video from YouTube or Vimeo</Button>
          </div>
        );
      },
      {
        title: t(translationKeys.AddMediaModalAddButtonTitle),
        closable: true,
        ...bem('add-media-modal-wrapper'),
        buttons: () => (
          <div>
            <Button variant={'default'} onClick={() => close?.()}>
              Cancel
            </Button>
          </div>
        ),
      }
    );

    close?.();
  }

  async function openAddVideoModal(closeAddMediaModal: Function) {
    if (
      await UI.confirm(
        <div {...bem('add-video-modal')}>
          <p>Enter video link</p>

          <Form
            schema={addVideoFormSchema}
            onChange={values => (videoLink.current = values[AddVideoInputNames.VIDEO_LINK])}
            initialValues={{ [AddVideoInputNames.VIDEO_LINK]: videoLink.current }}
          />

          <p>To use a video from your phone, upload it to YouTube or Vimeo first.</p>
        </div>,
        {
          okText: t(coreTranslationKeys.core.continue),
          cancelText: t(coreTranslationKeys.core.goBack),
          ...bem('add-video-modal-wrapper'),
        }
      )
    ) {
      await openConfirmVideoModal(closeAddMediaModal);
      videoLink.current = undefined;
      closeAddMediaModal?.();
    }
  }

  async function openConfirmImageModal(croppedImage: string, closeAddMediaModal: Function) {
    if (
      await UI.confirm(
        <CaptionFormModal
          {...bem('confirm-image-modal')}
          onChange={value => {
            caption.current = value;
          }}
        >
          <div {...bem('image-container')}>
            <Image alt={'Cropped image'} src={croppedImage} />
          </div>
        </CaptionFormModal>,
        {
          title: t(translationKeys.ConfirmImageModalTitle),
          okText: t(coreTranslationKeys.core.continue),
          cancelText: t(coreTranslationKeys.core.goBack),
          ...bem('confirm-image-modal-wrapper'),
        }
      )
    ) {
      uploadedMediaHandler.push({
        type: 'photo',
        url: croppedImage,
        caption: caption.current,
      });
      caption.current = undefined;
      closeAddMediaModal?.();
    } else {
      imagePickerRef.current?.remove();
    }
  }

  async function openConfirmVideoModal(closeAddMediaModal: Function) {
    if (
      await UI.confirm(
        <CaptionFormModal
          {...bem('confirm-video-modal')}
          onChange={value => {
            caption.current = value;
          }}
        >
          <KeepProportions {...bem('video-container')}>
            <EmbeddedVideo url={videoLink.current} />
          </KeepProportions>
        </CaptionFormModal>,
        {
          title: t(translationKeys.ConfirmVideoModalTitle),
          okText: t(coreTranslationKeys.core.continue),
          cancelText: t(coreTranslationKeys.core.goBack),
          ...bem('confirm-video-modal-wrapper'),
        }
      )
    ) {
      uploadedMediaHandler.push({
        type: 'video',
        url: videoLink.current,
        caption: caption.current,
        thumbnailUrl: await getThumbnailUrl(videoLink.current),
      });
      closeAddMediaModal?.();
    }
    videoLink.current = undefined;
    caption.current = undefined;
  }

  function handleRemoveAttachment(i: number) {
    if (selectedUpdate) {
      mediaToBeRemoved.current?.push(uploadedMedia[i].id);
      // media to be added will not have id so we can check against that in the api call
    }
    uploadedMediaHandler.remove(i);
  }

  // TODO disable on invalid form
  function buttons(valid: boolean, submitting: boolean) {
    const buttonsDisabled = submitting || !valid;

    function onClick() {
      if (!buttonsDisabled) {
        handlePostUpdate();
      }
    }

    return (
      <Button type="submit" disabled={buttonsDisabled} loading={submitting} onClick={onClick}>
        {t(
          selectedUpdate
            ? translationKeys.EditUserUpdateModalButtonLabel
            : translationKeys.AddUserUpdateModalButtonLabel
        )}
      </Button>
    );
  }

  return (
    <div {...bem(null, [{ loading }])}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Form
            schema={schema}
            initialValues={value}
            onChange={setValue}
            vertical
            render={({ valid, submitting }) => {
              return (
                <>
                  {schema.slice(0, 3).map((entry, key) => {
                    return <FormField key={key} name={entry.name} />;
                  })}

                  <div {...bem('attachments')}>
                    {uploadedMedia.map((media, i) => (
                      <Attachment
                        key={i + media.url}
                        media={media}
                        removeAttachment={() => handleRemoveAttachment(i)}
                      />
                    ))}
                  </div>

                  {uploadedMedia.length < 3 ? (
                    <Button onClick={openAddMediaModal}>Add photo or video</Button>
                  ) : (
                    <div>three attachments max.</div>
                  )}

                  {!selectedUpdate &&
                    schema.slice(3).map((entry, key) => {
                      return <FormField key={key} name={entry.name} />;
                    })}

                  {buttonsPortal?.current ? (
                    ReactDOM.createPortal(buttons(valid, submitting), buttonsPortal.current)
                  ) : (
                    <div {...bem('button-wrapper')}>{buttons(valid, submitting)}</div>
                  )}
                </>
              );
            }}
          />
        </>
      )}
    </div>
  );
}

interface IAttachmentProps {
  media: InkindUserUpdateMedia;
  removeAttachment: () => void;
}

function Attachment({ media, removeAttachment }: IAttachmentProps) {
  const bem = useBemCN(blockName + '-attachment');

  function getAttachment() {
    switch (media.type) {
      case 'photo':
        return <Image {...bem('attachment', [{ [media.type]: true }])} src={media.url} alt={media.caption} />;
      case 'video':
        return <Image {...bem('attachment', [{ [media.type]: true }])} src={media.thumbnailUrl} alt={media.caption} />;
    }
  }

  return (
    <div {...bem()}>
      <div {...bem('remove-button')}>
        <Button variant={'danger'} size={'xs'} circle tooltip={'Remove attachment'} onClick={removeAttachment}>
          <SvgIcon name={'X'} />
        </Button>
      </div>
      {getAttachment()}
    </div>
  );
}

interface IShareOnSocialMediaProps {
  updateId: string;
  shareOnFacebook?: boolean;
  shareOnTwitter?: boolean;
  sharedToEmail?: boolean;
  buttonsRef?: React.MutableRefObject<HTMLDivElement>;
}

function ShareUpdateOnSocialMedia({
  updateId,
  shareOnFacebook,
  shareOnTwitter,
  sharedToEmail,
  buttonsRef,
}: IShareOnSocialMediaProps) {
  const [sharedToFacebook, setSharedToFacebook] = React.useState<boolean>(false);
  const [sharedToTwitter, setSharedToTwitter] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const updateLink = React.useMemo(
    () => updateId && encodeURIComponent(getUserUpdateLink({ id: updateId } as InkindUserUpdate)),
    [updateId]
  );

  function handleShareToFacebookClicked() {
    const shareUrl = createFacebookShareUrl(updateLink);
    openExternalLink(shareUrl);
    setSharedToFacebook(true);

    if (!shareOnTwitter) {
      UI.closeDialog();
    }
  }

  function handleShareToTwitterClicked() {
    const shareUrl = createTwitterShareUrl(updateLink);
    openExternalLink(shareUrl);
    setSharedToTwitter(true);

    UI.closeDialog();
  }

  const buttons = (
    <>
      {shareOnFacebook && !sharedToFacebook && (
        <Button
          variant="facebook"
          onClick={handleShareToFacebookClicked}
          prepend={<SvgIcon set={'brand-icons'} name={'FacebookWhite'} />}
        >
          Finish posting
        </Button>
      )}
      {shareOnTwitter &&
        ((!shareOnFacebook && !sharedToTwitter) || (shareOnFacebook && sharedToFacebook && !sharedToTwitter)) && (
          <Button
            variant="twitter"
            onClick={handleShareToTwitterClicked}
            prepend={<SvgIcon set={'brand-icons'} name={'TwitterWhite'} />}
          >
            Finish Posting
          </Button>
        )}
    </>
  );

  return (
    <div>
      <p>
        {t(
          sharedToEmail ? translationKeys.UpdateSentToPageEmailAndFollowersLabel : translationKeys.UpdateSentToPageLabel
        )}
      </p>
      <p>
        <HTMLParser rawHtml={t(translationKeys.OneMoreStep)} /> {shareOnFacebook && !sharedToFacebook && 'Facebook'}
        {shareOnTwitter &&
          ((!shareOnFacebook && !sharedToTwitter) || (shareOnFacebook && sharedToFacebook && !sharedToTwitter)) &&
          'Twitter'}
      </p>

      {buttonsRef?.current ? (
        ReactDOM.createPortal(buttons, buttonsRef.current)
      ) : (
        <div className={'tw-flex tw-flex-row tw-justify-center'}>{buttons}</div>
      )}
    </div>
  );
}

interface ICaptionFormModalProps {
  className?: string;
  onChange?: (value: string) => void;
}

function CaptionFormModal({ className, onChange = noop, children }: React.PropsWithChildren<ICaptionFormModalProps>) {
  const captionFieldName = 'caption';

  const schema: FormSchemaEntry[] = [
    {
      name: captionFieldName,
      placeholder: 'Caption',
      type: 'text',
    },
  ];

  function handleChange(value: { [captionFieldName]: string }) {
    onChange(value[captionFieldName]);
  }

  return (
    <div className={className}>
      {children}
      <Form
        onChange={handleChange}
        schema={schema}
        render={() => {
          return (
            <>
              {schema.map((field, key) => (
                <FormField key={key} name={field.name} />
              ))}
            </>
          );
        }}
      />
    </div>
  );
}
